import React from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import styles from './SparklineChart.module.sass';

// log10(x + 1) — same log1p transform the Detail chart uses, so all charts in
// the analytics view compress spikes consistently and zero still plots at the
// baseline (log10(0 + 1) = 0).
const toLog = (value: number): number => Math.log10(Math.max(value, 0) + 1);

interface SparklineChartProps {
  data: number[];
  width?: number | string;
  height?: number;
  lineColor?: string;
  fillColor?: string;
  loading?: boolean;
}

const SparklineChart: React.FC<SparklineChartProps> = ({
  data,
  width = 220,
  height = 52,
  lineColor = styles.defaultLine,
  fillColor = styles.defaultFill,
  loading = false,
}) => {
  const isSinglePoint = data.length === 1;
  const safeData = data.length === 0
    ? [0, 0, 0, 0, 0, 0, 0]
    : data;
  const chartData = safeData.map((value, index) => ({
    index,
    value: toLog(value),
  }));
  const singlePointYMax = Math.max(toLog(safeData[0] || 0) * 1.25, toLog(1));

  const chartWidth = typeof width === 'number' ? `${width}px` : width;

  return (
    <div
      style={{ width: chartWidth, height, position: 'relative' }}
      role="img"
      aria-label={loading ? 'Loading metrics' : 'Metrics trend sparkline'}
      aria-busy={loading ? true : undefined}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 1, right: 1, bottom: 1, left: 1 }}>
          {isSinglePoint && (
            <>
              <XAxis dataKey="index" hide={true} />
              <YAxis hide={true} domain={[0, singlePointYMax]} tickCount={3} />
              <CartesianGrid stroke={styles.grid} vertical={false} strokeDasharray="2 3" />
            </>
          )}
          <Area
            type="monotone"
            dataKey="value"
            stroke={lineColor}
            strokeWidth={2.5}
            fill={fillColor}
            fillOpacity={0.9}
            dot={isSinglePoint ? { r: 3, fill: lineColor, stroke: styles.dotStroke, strokeWidth: 1.5 } : false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
      {loading && <div className={styles.skeletonOverlay} aria-hidden={true} />}
    </div>
  );
};

export default SparklineChart;
